import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouteContext, useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { buildExternalMapUrl, type MediaUnitDetail } from '@ooh/contracts';
import { MediaUnitPopupCard } from '@ooh/ui/components/media-unit-popup-card';
import { Button } from '@ooh/ui/components/button';
import { mediaUnitDetailQuery } from '../api/queries';
import { NearbyPoisSection } from '../../pois/components/nearby-pois-section';
import { pickSharedFilterSearch } from '../../../lib/filter-config';
import { encodeCatalogSearch, extractViewBbox } from '../../../lib/catalog-return-context';

import { TransportNearbySection } from '@ooh/screens';

interface MapPinPopupCardProps {
  pinId: string | number;
  onClose: () => void;
  /** Whether the public transport layer toggle is enabled. */
  isPublicEnabled?: boolean;
  /** Whether the major roads layer toggle is enabled. */
  isRoadsEnabled?: boolean;
  /** Translated title for the nearby transport section. */
  nearbyTitle?: string;
  quickAddAction?: React.ReactNode;
}

function PopupSkeleton() {
  return (
    <div data-testid="pin-popup-skeleton" className="min-w-[280px] max-w-[340px] space-y-2 p-2">
      <div className="h-[140px] rounded bg-muted animate-pulse" />
      <div className="h-4 w-3/4 rounded bg-muted animate-pulse" />
      <div className="h-3 w-1/2 rounded bg-muted animate-pulse" />
      <div className="h-3 w-2/3 rounded bg-muted animate-pulse" />
      <div className="h-3 w-1/2 rounded bg-muted animate-pulse" />
    </div>
  );
}

interface PopupErrorProps {
  message: string;
  retryLabel: string;
  onRetry: () => void;
}

function PopupError({ message, retryLabel, onRetry }: PopupErrorProps) {
  return (
    <div
      data-testid="pin-popup-error"
      className="min-w-[280px] max-w-[340px] p-4 text-center space-y-3"
    >
      <p className="text-sm text-muted-foreground">{message}</p>
      <Button variant="outline" size="sm" data-testid="pin-popup-retry" onClick={onRetry}>
        {retryLabel}
      </Button>
    </div>
  );
}

function buildLabels(t: ReturnType<typeof useTranslation<'map'>>['t']) {
  return {
    close: t('pinPopup.close'),
    details: t('pinPopup.details'),
    maps: t('pinPopup.maps'),
    format: t('pinPopup.format'),
    dimensions: t('pinPopup.dimensions'),
    area: t('pinPopup.area'),
    supplier: t('pinPopup.supplier'),
    catalogPrice: t('pinPopup.catalogPrice'),
    illumination: t('pinPopup.illumination'),
    illuminated: t('pinPopup.illuminated'),
    notIlluminated: t('pinPopup.notIlluminated'),
    noPhoto: t('pinPopup.noPhoto'),
    photoAlt: t('pinPopup.photoAlt'),
  };
}

interface PopupLoadedProps {
  unit: MediaUnitDetail;
  onClose: () => void;
  t: ReturnType<typeof useTranslation<'map'>>['t'];
  isPublicEnabled?: boolean;
  isRoadsEnabled?: boolean;
  nearbyTitle?: string;
  quickAddAction?: React.ReactNode;
}

// eslint-disable-next-line complexity
function PopupLoaded({
  unit,
  onClose,
  t,
  isPublicEnabled = false,
  isRoadsEnabled = false,
  nearbyTitle = '',
  quickAddAction,
}: PopupLoadedProps) {
  const navigate = useNavigate();
  // Current /map route search — narrowed to the shared filter fields plus
  // `viewBbox` (the persisted map viewport) and forwarded as an opaque
  // `catalogSearch` param, so the sidebar "Mapa" link can restore both after
  // a "Szczegóły" -> unit detail -> "Mapa" round trip (CU-86cb1nq31).
  const mapSearch = useSearch({ strict: false, structuralSharing: false }) as Record<
    string,
    unknown
  >;
  const googleMapsUrl =
    unit.latitude != null && unit.longitude != null
      ? buildExternalMapUrl(unit.latitude, unit.longitude)
      : undefined;

  const handleDetails = () => {
    const sharedFilters = pickSharedFilterSearch(mapSearch);
    const viewBbox = extractViewBbox(mapSearch);
    const catalogSearch = encodeCatalogSearch(
      viewBbox ? { ...sharedFilters, viewBbox } : sharedFilters,
    );
    navigate({
      to: '/media-units/$id',
      params: { id: String(unit.id) },
      search: { catalogSearch },
    });
  };

  return (
    <MediaUnitPopupCard
      grwId={unit.grwId}
      formatLabel={unit.formatLabel ?? ''}
      widthM={unit.widthM}
      heightM={unit.heightM}
      areaM2={unit.areaSqm}
      supplierName={unit.supplierName}
      catalogPrice={unit.catalogPrice}
      illuminated={unit.illuminated}
      address={unit.address ?? ''}
      city={unit.city}
      onDetails={handleDetails}
      googleMapsUrl={googleMapsUrl}
      photoUrl={unit.photos?.[0]?.displayUrl}
      onClose={onClose}
      labels={buildLabels(t)}
      quickAddAction={quickAddAction}
      nearbyPoisSection={
        <>
          <NearbyPoisSection mediaUnitId={unit.id} />
          <TransportNearbySection
            mediaUnitId={unit.id}
            busEnabled={isPublicEnabled}
            roadsEnabled={isRoadsEnabled}
            nearbyTitle={nearbyTitle}
          />
        </>
      }
    />
  );
}

export function MapPinPopupCard({
  pinId,
  onClose,
  isPublicEnabled = false,
  isRoadsEnabled = false,
  nearbyTitle,
  quickAddAction,
}: MapPinPopupCardProps) {
  const { auth } = useRouteContext({ from: '__root__' });
  const { t } = useTranslation('map');

  const { data, isLoading, isError, refetch } = useQuery({
    ...mediaUnitDetailQuery(auth, pinId),
    staleTime: 0,
    // No automatic retries: the popup shows an error state with a manual retry
    // button, so letting TanStack Query auto-retry would bypass the error UI.
    retry: false,
  });

  if (isLoading) return <PopupSkeleton />;
  if (isError) {
    return (
      <PopupError
        message={t('pinPopup.loadError')}
        retryLabel={t('pinPopup.retry')}
        onRetry={() => refetch()}
      />
    );
  }
  if (!data) return null;

  return (
    <PopupLoaded
      unit={data.data}
      onClose={onClose}
      t={t}
      isPublicEnabled={isPublicEnabled}
      isRoadsEnabled={isRoadsEnabled}
      nearbyTitle={nearbyTitle}
      quickAddAction={quickAddAction}
    />
  );
}
